<template>
    <section id="Product">
        <div
            class="hidden print:flex print:pb-8 print:pt-8 print:justify-center"
        >
            <img
                :src="marketingAssetUrl(product.vendor.logo)"
                class="print:max-w-[300px]"
                :alt="'image-' + product.vendor.name"
            />
        </div>
        <div
            ref="breadcrumbWrapper"
            class="hidden at1024:flex w-full justify-between fixed sticky_nav_top z-[1] bg-white duration-150 border-b print:!hidden"
        >
            <Breadcrumbs
                :crumbs="crumbs"
                class="!border-b-0"
                h2style="hidden"
                :title="product.sku"
            />
            <div v-if="user" class="flex items-center gap-3 mr-8 shrink-0">
                <button
                    class="btn-black-on-gray-pill-alt"
                    @click.stop.prevent="toggleShareDialog($event, product)"
                >
                    <BaseSvg
                        name="icon-short-cut"
                        class="h-6 w-6"
                        aria-hidden="true"
                    />
                    {{ $t("Share Cut Sheet") }}
                </button>
                <button
                    class="btn-black-on-gray-pill-alt"
                    @click="togglePrintDialog($event, '')"
                >
                    <BaseSvg name="icon-printer" aria-hidden="true" />
                    {{ $t("Print") }}
                </button>
            </div>
        </div>

        <div
            class="flex flex-col gap-4 px-4 relative z-0 at1024:flex-row at1024:gap-8 at1024:pl-8 at1024:pr-0 at1024:mb-6"
            :class="user ? '' : '!border-b'"
        >
            <div
                class="shrink-0 h-fit w-32 at1024:pb-8 print:hidden at1024:print:hidden"
                :class="user ? 'sticky top-[183px]' : 'relative top-24'"
            >
                <SideNav
                    v-model="isCurrent"
                    :click-to-scroll="true"
                    :sections="sections"
                    @scroll-to="scrollToSection"
                />
            </div>
            <div class="grow">
                <div
                    class="mt-4 mb-8 at1024:flex at1024:justify-between at1024:mt-28 at1024:pr-16 w-full"
                >
                    <div
                        class="at1024:relative at1024:h-auto at1024:w-[calc(100%_-_348px)] at1350:w-[calc(100%_-_448px)] at1440:w-[calc(100%_-_465px)]"
                    >
                        <div class="at1024:bg-gray-100 at1024:p-10">
                            <header
                                class="flex gap-2 items-start justify-center at1024:justify-between"
                            >
                                <!-- Links -->
                                <div
                                    class="text-xs max-w-[calc(100%_-_96px)] at768:text-sm at1024:max-w-[288px] at1200:max-w-[364px] at1350:max-w-[514px] at1440:max-w-[604px] at1550:max-w-[714px]"
                                >
                                    <div
                                        v-if="
                                            product?.vendor?.display_name &&
                                            product.series?.name
                                        "
                                        class="truncate at1024:text-base"
                                    >
                                        <!-- Top: manufacturer -->
                                        <Link
                                            :href="
                                                route(
                                                    'web.catalog.manufacturers.show',
                                                    product?.vendor?.slug
                                                )
                                            "
                                            class="font-medium hover:border-b border-black"
                                        >
                                            {{ product?.vendor?.display_name }}
                                        </Link>
                                        <span class="text-gray-400"> / </span>

                                        <!-- Top: series -->
                                        <Link
                                            class="hover:border-b border-black"
                                            :href="series_route"
                                        >
                                            {{ product.series?.name }}
                                        </Link>
                                    </div>
                                    <div
                                        v-if="
                                            product.tags?.['item-category'] &&
                                            product.tags?.['item-sub-category']
                                        "
                                        class="hidden at1024:block at1024:truncate"
                                    >
                                        <!-- Bottom: category -->
                                        <Link
                                            :href="category_route"
                                            class="text-gray-400 hover:type-primary hover:underline"
                                        >
                                            {{
                                                product.tags["item-category"][0]
                                                    ?.name
                                            }}
                                        </Link>
                                        <span class="text-gray-325"> / </span>
                                        <!-- Bottom: sub-category -->
                                        <Link
                                            :href="sub_category_route"
                                            class="text-gray-400 hover:type-primary hover:underline"
                                        >
                                            {{
                                                product.tags[
                                                    "item-sub-category"
                                                ][0]?.name
                                            }}
                                        </Link>
                                    </div>
                                </div>
                                <!-- Buttons -->
                                <div
                                    v-if="user"
                                    class="hidden at1024:flex at1024:gap-2"
                                >
                                    {{ product.id }}
                                    <!-- Pin component -->
                                    <Pin
                                        :id="product.item_reference_id"
                                        :disabled="!product"
                                        type="item"
                                        class="!w-auto"
                                    />
                                    <!-- Add component -->
                                    <button
                                        :disabled="
                                            !card || !availability || !pricing
                                        "
                                        class="btn-icon-black-outline-pill p-0"
                                        @click="
                                            toggleProductOptionsDialog($event, {
                                                card,
                                                availability,
                                                pricing,
                                            })
                                        "
                                    >
                                        <BaseSvg
                                            name="icon-addition-sign"
                                            class="h-10 p-2 w-10"
                                        />
                                    </button>
                                </div>
                            </header>

                            <div
                                v-if="user"
                                data-dialog="true"
                                class="flex justify-end at1024:hidden print:hidden"
                            >
                                <button
                                    class="w-10 h-10 mr-4 flex justify-center rounded-full hover:bg-gray-120 active:bg-gray-150 shrink-0"
                                    @click="
                                        toggleMobileOptionsDialog($event, {
                                            card,
                                            availability,
                                            pricing,
                                        })
                                    "
                                >
                                    <BaseSvg
                                        name="icon-more-vert"
                                        class="p-2 print:!hidden"
                                        aria-hidden="true"
                                    />
                                    <span class="sr-only">Show options</span>
                                </button>
                            </div>

                            <figure data-section="0" class="sentinel">
                                <ProductImageCarousel
                                    :product="product"
                                    :user="user"
                                />
                            </figure>
                        </div>

                        <ProductSummary
                            :card="card"
                            :product="product"
                            :pricing="pricing"
                            :availability="availability"
                            :options="options"
                            :finish="finish"
                            :hide-cost-during-print="hideCostDuringPrint"
                            :is-loading-meta="isLoadingMeta"
                            :finish-swatch="finish_swatch"
                            :master_note_restriction="master_note_restriction"
                            :show-build-button="false"
                            :surcharge="surcharge"
                            class="h-full block bg-white at1024:hidden at1024:print:block print:h-auto"
                        />
                        <div
                            v-if="user"
                            class="hidden at1024:block print:block"
                        >
                            <div
                                id="Specs"
                                class="relative pt-8 at1024:pt-12 sentinel"
                                data-section="1"
                            >
                                <SpecsTable
                                    :rows="
                                        product['grouped_attributes'][
                                            'product-specs'
                                        ]
                                    "
                                    :attributes="product.attributes"
                                    :primary-attributes="primaryAttributes"
                                    title="Product Specs"
                                >
                                    <div v-if="finishInfo?.length" class="mb-8">
                                        <h3 class="font-bold text-xl mt-6 mb-2">
                                            {{ $t("Finish") }}
                                        </h3>
                                        <ul
                                            class="text-base at1440:grid at1440:grid-cols-3 print:!text-xs"
                                        >
                                            <li v-for="row in finishInfo">
                                                <Divider
                                                    class="block at1440:hidden"
                                                />
                                                <div
                                                    class="py-3 pl-0 grid grid-cols-2 at1440:block"
                                                >
                                                    <div
                                                        class="type-primary at1440:font-medium"
                                                    >
                                                        {{ row.label }}
                                                    </div>
                                                    <button
                                                        v-if="
                                                            row.label ==
                                                            'Finish'
                                                        "
                                                        @click="
                                                            openArticle(
                                                                config
                                                                    .intercomArticles
                                                                    .finishes.id
                                                            )
                                                        "
                                                        class="w-fit type-primary hover:text-red underline"
                                                    >
                                                        {{ row.value }}
                                                    </button>
                                                    <span
                                                        v-else-if="row.value"
                                                        >{{ row.value }}</span
                                                    >
                                                    <span v-else>—</span>
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                </SpecsTable>
                                <SpecsTable
                                    v-if="
                                        _.get(
                                            product,
                                            'grouped_attributes.technical-specs',
                                            false
                                        )
                                    "
                                    :rows="
                                        product['grouped_attributes'][
                                            'technical-specs'
                                        ]
                                    "
                                    :attributes="product.attributes"
                                    title="Technical Specs"
                                />
                                <SpecsTable
                                    v-if="
                                        _.get(
                                            product,
                                            'grouped_attributes.dimensions',
                                            false
                                        )
                                    "
                                    :rows="
                                        product['grouped_attributes'][
                                            'dimensions'
                                        ]
                                    "
                                    :attributes="product.attributes"
                                    title="Dimensions"
                                    class="at1440:!border-b-[1px]"
                                />
                            </div>

                            <div
                                v-if="
                                    product.series &&
                                    product.series?.['descriptions']?.length !==
                                        0
                                "
                                id="Overview"
                                class="relative mt-12 p-8 at1024:bg-gray-100 sentinel print:mt-0 print:py-8 at1024:print:px-0 at1350:p-16"
                                data-section="2"
                            >
                                <Overview :product="product" />
                            </div>

                            <div
                                v-show="
                                    resources && Object.keys(resources)?.length
                                "
                                id="Resources"
                                class="px-4 at1024:px-0 relative pt-8 sentinel print:hidden"
                                data-section="3"
                            >
                                <h3 class="font-bold text-xl pb-3 pt-4 mb-2">
                                    {{ $t("Resources") }}
                                </h3>
                                <ResourceList :resources="resources" />
                            </div>

                            <div
                                id="Parts"
                                class="w-full px-4 at1024:px-0 relative pt-8 sentinel print:hidden"
                                data-section="4"
                                v-if="product.series"
                            >
                                <PartsBySeries :series="product.series" />
                            </div>
                        </div>
                        <div
                            v-if="user"
                            class="block at1024:hidden mt-24 print:hidden"
                        >
                            <div id="smallSpecs" class="px-0 relative">
                                <button
                                    class="w-full font-bold flex justify-between items-center py-5 px-3.5 border-t"
                                    @click="
                                        isSeen === 'smallSpecs'
                                            ? (isSeen = false)
                                            : (isSeen = 'smallSpecs')
                                    "
                                >
                                    <span>{{ $t("Specs") }}</span>
                                    <BaseSvg
                                        :name="
                                            isSeen === 'smallSpecs'
                                                ? 'icon-subtraction-sign'
                                                : 'icon-addition-sign'
                                        "
                                        aria-hidden="true"
                                        class="border border-black rounded-full w-4 h-4"
                                    />
                                </button>
                                <div
                                    v-if="isSeen === 'smallSpecs'"
                                    class="px-4"
                                >
                                    <SpecsTable
                                        :rows="
                                            product['grouped_attributes'][
                                                'product-specs'
                                            ]
                                        "
                                        :attributes="product.attributes"
                                        :primary-attributes="primaryAttributes"
                                        title="Product Specs"
                                    >
                                        <div v-if="finishInfo" class="mb-8">
                                            <h3 class="font-bold text-lg my-3">
                                                {{ $t("Finish") }}
                                            </h3>
                                            <ul
                                                class="text-base at1440:grid at1440:grid-cols-3"
                                            >
                                                <li v-for="row in finishInfo">
                                                    <Divider
                                                        class="block at1440:hidden"
                                                    />
                                                    <div
                                                        class="py-3 pl-0 grid grid-cols-2 at1440:block"
                                                    >
                                                        <div
                                                            class="type-primary at1440:font-medium"
                                                        >
                                                            {{ row.label }}
                                                        </div>
                                                        <Link
                                                            v-if="row.route"
                                                            :href="
                                                                route(
                                                                    row.route
                                                                        .name
                                                                )
                                                            "
                                                            class="w-fit type-primary hover:text-red underline"
                                                        >
                                                            {{
                                                                row.value
                                                                    ? row.value
                                                                    : "–"
                                                            }}
                                                        </Link>
                                                        <div
                                                            v-else
                                                            class="type-secondary"
                                                        >
                                                            {{
                                                                row.value
                                                                    ? row.value
                                                                    : "–"
                                                            }}
                                                        </div>
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>
                                    </SpecsTable>

                                    <SpecsTable
                                        v-if="
                                            _.get(
                                                product,
                                                'grouped_attributes.technical-specs',
                                                false
                                            )
                                        "
                                        :rows="
                                            product['grouped_attributes'][
                                                'technical-specs'
                                            ]
                                        "
                                        :attributes="product.attributes"
                                        title="Technical Specs"
                                    />

                                    <SpecsTable
                                        v-if="
                                            _.get(
                                                product,
                                                'grouped_attributes.dimensions',
                                                false
                                            )
                                        "
                                        :rows="
                                            product['grouped_attributes'][
                                                'dimensions'
                                            ]
                                        "
                                        :attributes="product.attributes"
                                        :title="$t('Dimensions')"
                                        class="at1440:border-b-[1px]"
                                    />
                                </div>
                            </div>

                            <div
                                v-if="
                                    product.series?.['descriptions']?.length !==
                                    0
                                "
                                id="smallOverview"
                                class="px-0 relative"
                            >
                                <button
                                    class="w-full font-bold flex justify-between items-center py-5 px-3.5 border-t"
                                    @click="
                                        isSeen === 'smallOverview'
                                            ? (isSeen = false)
                                            : (isSeen = 'smallOverview')
                                    "
                                >
                                    <span>{{ $t("Overview") }}</span>
                                    <BaseSvg
                                        :name="
                                            isSeen === 'smallOverview'
                                                ? 'icon-subtraction-sign'
                                                : 'icon-addition-sign'
                                        "
                                        aria-hidden="true"
                                        class="border border-black rounded-full w-4 h-4"
                                    />
                                </button>
                                <div
                                    v-if="isSeen === 'smallOverview'"
                                    class="pb-8 px-4"
                                >
                                    <Overview :product="product" />
                                </div>
                            </div>

                            <div
                                v-show="
                                    resources && Object.keys(resources).length
                                "
                                id="smallResources"
                                class="px-0 relative"
                            >
                                <button
                                    class="w-full font-bold flex justify-between items-center py-5 px-3.5 border-t"
                                    @click="
                                        isSeen === 'smallResources'
                                            ? (isSeen = false)
                                            : (isSeen = 'smallResources')
                                    "
                                >
                                    <span>{{ $t("Resources") }}</span>
                                    <BaseSvg
                                        :name="
                                            isSeen === 'smallResources'
                                                ? 'icon-subtraction-sign'
                                                : 'icon-addition-sign'
                                        "
                                        aria-hidden="true"
                                        class="border border-black rounded-full w-4 h-4"
                                    />
                                </button>

                                <div
                                    v-if="isSeen === 'smallResources'"
                                    class="px-4 mb-8"
                                >
                                    <div class="overflow-shadows-horizontal">
                                        <ul class="text-base">
                                            <li
                                                v-for="(
                                                    resourceGroup, groupType
                                                ) in resources"
                                                :key="groupType"
                                                class="text-base at1200:text-lg block gap-2 py-4 pl-0 border-b"
                                            >
                                                <button
                                                    class="type-primary hover:text-black flex items-center gap-3 font-medium capitalize"
                                                    @click.prevent="
                                                        resourceOpen ===
                                                        resourceGroup[0]
                                                            ? (resourceOpen = false)
                                                            : (resourceOpen =
                                                                  resourceGroup[0])
                                                    "
                                                >
                                                    <BaseSvg
                                                        v-if="
                                                            resourceOpen ===
                                                            resourceGroup[0]
                                                        "
                                                        :name="
                                                            resourceOpen ===
                                                            resourceGroup[0]
                                                                ? 'icon-subtraction-sign'
                                                                : 'icon-addition-sign'
                                                        "
                                                        aria-hidden="true"
                                                        class="w-4 h-4 fill-white p-0.5 bg-gray rounded-full"
                                                    />
                                                    <BaseSvg
                                                        v-else
                                                        name="icon-addition-sign"
                                                        class="w-4 h-4 fill-white p-0.5 bg-gray rounded-full"
                                                    />
                                                    {{ groupType }}
                                                </button>
                                                <Transition name="fade">
                                                    <ul
                                                        v-if="
                                                            resourceOpen ===
                                                            resourceGroup[0]
                                                        "
                                                        class="py-2 w-full"
                                                    >
                                                        <li
                                                            v-for="(
                                                                resource, i
                                                            ) in resourceGroup"
                                                            :key="i"
                                                        >
                                                            <button
                                                                class="flex items-center gap-3 py-2 whitespace-nowrap text-base"
                                                                @click.stop.prevent="
                                                                    getResource(
                                                                        $event,
                                                                        resource
                                                                    )
                                                                "
                                                            >
                                                                <BaseSvg
                                                                    :name="
                                                                        resource
                                                                            .asset
                                                                            .source ===
                                                                        3
                                                                            ? 'icon-description'
                                                                            : 'icon-movie'
                                                                    "
                                                                    aria-hidden="true"
                                                                    :class="{
                                                                        'w-6':
                                                                            resource
                                                                                .asset
                                                                                .source !==
                                                                            3,
                                                                    }"
                                                                />
                                                                <span
                                                                    class="text-left hover:underline underline-offset-4 capitalize truncate max-w-sm"
                                                                >
                                                                    {{
                                                                        resource.name
                                                                    }}
                                                                </span>
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </Transition>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div
                                id="smallParts"
                                class="px-0 relative print:hidden"
                            >
                                <button
                                    class="w-full font-bold flex justify-between items-center py-5 px-3.5 border-t"
                                    @click="
                                        isSeen === 'smallParts'
                                            ? (isSeen = false)
                                            : (isSeen = 'smallParts')
                                    "
                                >
                                    <span>{{ $t("Parts") }}</span>
                                    <BaseSvg
                                        :name="
                                            isSeen === 'smallParts'
                                                ? 'icon-subtraction-sign'
                                                : 'icon-addition-sign'
                                        "
                                        class="border border-black rounded-full w-4 h-4"
                                    />
                                </button>
                                <div v-if="isSeen === 'smallParts'" class="p-4">
                                    <div
                                        v-if="!components?.length"
                                        class="text-center flex items-center justify-center h-56 bg-gray-120 text-gray"
                                    >
                                        <div class="block">
                                            <div class="font-medium text-base">
                                                {{
                                                    $t(
                                                        "We haven't listed parts here yet."
                                                    )
                                                }}
                                            </div>
                                            <div class="text-gray text-sm">
                                                {{ $t("Start a") }}
                                                <button
                                                    class="text-gray-600 underline underline-offset-4 hover:text-black"
                                                    @click.stop.prevent="
                                                        showNewMessage
                                                    "
                                                >
                                                    {{ $t("Live Chat") }}
                                                </button>
                                                {{
                                                    $t("to find what you need")
                                                }}.
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else>
                                        <SearchInput
                                            v-model="partSearch"
                                            :placeholder="$t('Search')"
                                            class="mb-4"
                                        />
                                        <div v-if="filteredComponents.length">
                                            <TableNew
                                                name="parts-table"
                                                :collapsable="true"
                                                :rows="filteredComponents"
                                                :columns="componentsColumns"
                                                :row-component="
                                                    markRaw(TableRowThree)
                                                "
                                                sub-row-path="sub_components"
                                                row-identifier="id"
                                            />
                                        </div>
                                        <RecordsNotFound v-else />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        class="h-fit bg-white relative overflow-y-auto shrink-0 w-[320px] hidden at1024:block print:hidden at1350:max-w-md at1350:w-[420px]"
                        :class="
                            user
                                ? 'sticky top-[183px]'
                                : 'relative !top-0 h-[500px]'
                        "
                    >
                        <ProductSummary
                            :card="card"
                            :product="product"
                            :pricing="pricing"
                            :availability="availability"
                            :options="options"
                            :is-loading-meta="isLoadingMeta"
                            :finish="finish"
                            :finish_swatch="finish_swatch"
                            :master_note_restriction="master_note_restriction"
                            :hide-cost-during-print="hideCostDuringPrint"
                            :show-build-button="true"
                            :surcharge="surcharge"
                        />
                    </div>
                </div>
                <div
                    v-if="user"
                    v-show="companions?.length"
                    id="Related"
                    class="mb-8 sentinel print:hidden"
                    data-section="5"
                >
                    <div class="at1024:pt-8 at1024:relative w-full h-[500px]">
                        <header class="flex items-center gap-8 mb-4">
                            <h3 class="font-bold text-2xl">
                                {{ $t("You might also need") }}
                            </h3>
                        </header>
                        <div
                            class="-mx-4 overflow-shadows-horizontal at1024:mx-0 at1024:w-[calc(100vw_-_192px)]"
                        >
                            <ul class="flex gap-8 items-center">
                                <template v-for="item in companions">
                                    <li
                                        class="first-of-type:ml-4 last-of-type:mr-4 at1024:first-of-type:ml-0 at1024:last-of-type:mr-0"
                                    >
                                        <AltProductCard
                                            :card="item"
                                            :availability="item.availability"
                                            :pricing="item.pricing"
                                            @click="
                                                eventBus.emit(
                                                    'dialog:toggle-product-preview',
                                                    {
                                                        event: $event,
                                                        data: { card: item },
                                                        sourcing: false,
                                                    }
                                                )
                                            "
                                        />
                                    </li>
                                </template>
                            </ul>
                        </div>
                    </div>
                </div>
                <div
                    v-if="user"
                    v-show="substitutes?.length"
                    id="Similar"
                    class="mb-8 sentinel print:hidden"
                    data-section="6"
                >
                    <div class="at1024:pt-8 at1024:relative w-full h-[500px]">
                        <header class="flex items-center gap-8 mb-4">
                            <h3 class="font-bold text-2xl">
                                {{ $t("Similar items") }}
                            </h3>
                            <!--                            <button class="flex items-center gap-1 hover:underline underline-offset-4">-->
                            <!--                                {{ $t('See all') }}-->
                            <!--                                <BaseSvg name="icon-chevron-right" />-->
                            <!--                            </button>-->
                        </header>
                        <div
                            class="-mx-4 overflow-shadows-horizontal at1024:mx-0 at1024:w-[calc(100vw_-_192px)]"
                        >
                            <ul class="flex gap-8 items-center">
                                <li
                                    v-for="item in substitutes"
                                    class="first-of-type:ml-4 last-of-type:mr-4 at1024:first-of-type:ml-0 at1024:last-of-type:mr-0"
                                >
                                    <AltProductCard
                                        :card="item"
                                        :availability="item.availability"
                                        :pricing="item.pricing"
                                        @click="
                                            eventBus.emit(
                                                'dialog:toggle-product-preview',
                                                {
                                                    event: $event,
                                                    data: { card: item },
                                                    sourcing: false,
                                                }
                                            )
                                        "
                                    />
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="!user" class="relative w-full h-fit">
            <ScrollObserver
                v-if="computerSeen !== true"
                class="absolute top-52 left-0 z-50"
                @in-viewport="computerSeen = true"
            />
            <div
                class="relative text-center at1024:text-left pt-32 overflow-hidden box-border"
                style="
                    min-height: 1000px;
                    background: linear-gradient(180deg, #fff, #bdbdbd);
                "
            >
                <svg
                    id="register-curve"
                    class="absolute top-[470px] fill-none left-0 w-full stroke-[#dcdcdc] stroke-[3px]"
                    viewBox="0 0 1234 2429"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none"
                >
                    <path
                        d="M1905.76 2426.98C1906.93 2412.96 1907.51 2398.8 1907.51 2384.51C1907.51 1981.82 1648.53 1806.09 564.012 1571.8C-633.809 1308.21 -1257 920.161 -1257 63.5162C-1257 42.8292 -1256.62 22.323 -1255.86 2.00008L-261.12 2.0281C-250.256 358.49 -9.85065 551.981 1017.24 766.404C2223.16 1029.99 2903 1352.14 2903 2303.97C2903 2345.63 2901.24 2386.65 2897.77 2427L1905.76 2426.98Z"
                    />
                </svg>

                <div
                    class="at1024:mx-auto px-4 flex flex-col items-center at1024:flex-row mx-auto max-w-3xl at1024:max-w-9xl relative"
                >
                    <div class="at1024:w-2/5">
                        <h2 class="text-2xl at1024:text-5xl mb-4 font-bold">
                            {{ $t("We are wholesale-only") }}.
                        </h2>
                        <p class="text-lg at1024:text-xl text-gray mb-12">
                            <span class="font-bold text-gray"
                                >{{
                                    $t(
                                        "As a wholesale distributor, we only sell to the trade"
                                    )
                                }}:</span
                            >
                            {{
                                $t(
                                    "we work with you, never against you. Register now to get exclusive access to the most comprehensive door hardware site in the industry"
                                )
                            }}.
                        </p>
                        <Link
                            :href="route('register')"
                            class="w-fit btn-white-on-black mx-auto mb-8 at1024:ml-0 at1024:mr-auto"
                        >
                            {{ $t("Register Now") }}
                        </Link>
                    </div>
                    <img
                        src="https://marketing-assets.seclock.com/image/upload/v1671205270/register_screen.png"
                        :class="computerSeen ? '' : 'translate-y-8'"
                        class="duration-500 at1024:h-[550px] at1200:h-[650px] w-auto at1024:left-[40%] at1024:top-0 at1024:absolute mb-0"
                        alt="register-screen"
                    />
                </div>

                <BaseSvg
                    name="icon-arrow-downward"
                    class="hidden at1024:block w-24 h-24 fill-white absolute right-16 bottom-16"
                />
                <svg
                    id="register-divider"
                    class="hidden at1024:block fill-black w-full relative h-[300px] mt-64 -bottom-0.5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 1234.33 494.31"
                    preserveAspectRatio="none"
                >
                    <path
                        d="M1234.33,493C718.38,368.12,203.81,278.94,0,0V494.31C298,494.31,686.45,494.31,1234.33,493Z"
                    />
                </svg>
            </div>
            <div
                class="z-0 pt-24 relative at1024:mx-auto text-center at1024:text-left text-white bg-black px-8 pb-24 at1024:pt-4 overflow-hidden bg-gradient-to-b from-black to-[#25282a]"
            >
                <div
                    style="z-index: -1"
                    class="absolute inset-0 bg-cover bg-no-repeat bg-shape-paths-black bg-top"
                />
                <div
                    class="px-8 at1024:px-0 mx-auto max-w-2xl at1024:max-w-6xl"
                >
                    <h3
                        class="at1024:max-w-4xl mb-4 text-2xl at1024:text-5xl font-bold"
                    >
                        {{
                            $t(
                                "Take advantage of all the account benefits on SECLOCK.com"
                            )
                        }}.
                    </h3>
                    <p class="at1024:max-w-xl text-lg at1024:text-xl mb-16">
                        {{
                            $t(
                                "In addition to gaining access to product-specific information such as pricing, availability, resources and full product details, take advantage of even more SECLOCK.com account benefits"
                            )
                        }}:
                    </p>
                    <ul
                        class="at1024:mx-auto at1024:max-w-6xl text-lg at1024:text-xl flex items-center at1024:items-start flex-col at1024:flex-row at1024:gap-6 mb-16"
                    >
                        <li class="at1024:w-1/3 mb-9">
                            <BaseSvg
                                class="mb-2 w-10 h-auto fill-gray"
                                name="icon-filter-alt"
                            />
                            <h5 class="font-bold text-white">
                                {{ $t("Our powerful product filter tool") }}
                            </h5>
                            <p class="text-gray-400">
                                {{
                                    $t(
                                        "Find the exact item you need by narrowing down your product search with our smart filters"
                                    )
                                }}.
                            </p>
                        </li>
                        <li class="at1024:w-1/3 mb-9">
                            <BaseSvg
                                class="mb-2 w-10 h-auto fill-gray"
                                name="icon-search"
                            />
                            <h5 class="font-bold text-white">
                                {{ $t("Find what you need with search") }}
                            </h5>
                            <p class="text-gray-400">
                                {{
                                    $t(
                                        "Start entering the part number or keyword for an item and our auto-suggest will take over"
                                    )
                                }}.
                            </p>
                        </li>
                        <li class="at1024:w-1/3 mb-9">
                            <BaseSvg
                                class="mb-2 w-10 h-auto fill-gray"
                                name="icon-phone-chat"
                            />
                            <h5 class="font-bold text-white">
                                {{ $t("Technical support at chat speed") }}
                            </h5>
                            <p class="text-gray-400">
                                {{
                                    $t(
                                        "Leverage the knowledge of the same technical sales reps that you speak to on the phone via our live chat tool"
                                    )
                                }}.
                            </p>
                        </li>
                    </ul>
                    <button
                        class="relative at1024:max-w-6xl at1024:mx-auto mb-16"
                        @click="
                            toggleVideoDialog(
                                $event,
                                'https://player.vimeo.com/video/519140524?autoplay=1'
                            )
                        "
                    >
                        <span
                            class="w-full group absolute inset-0 flex items-center justify-center"
                        >
                            <BaseSvg
                                class="duration-150 fill-white group-hover:fill-royal w-24 h-24"
                                name="icon-play-circle"
                            />
                        </span>
                        <img
                            src="https://marketing-assets.seclock.com/image/upload/v1655497319/register-vid-poster.jpg"
                            alt="register-video"
                        />
                    </button>
                    <div>
                        <h3
                            class="mx-auto max-w-3xl text-center mb-16 text-white font-bold text-2xl at1024:text-5xl"
                        >
                            {{
                                $t(
                                    "On the path to delivering exactly what you need"
                                )
                            }}.
                        </h3>

                        <div class="flex justify-center">
                            <Link
                                :href="route('register')"
                                class="btn-blue-on-gray"
                            >
                                {{ $t("Register Now") }}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import {
    computed,
    inject,
    markRaw,
    onBeforeUnmount,
    onMounted,
    ref,
} from "vue";
import {
    eventBus,
    useAssetTransforms,
    useChat,
    useMobileOptionsDialog,
    usePrintDialog,
    useProductOptionsDialog,
    useResources,
    useShareDialog,
    useUser,
    useVideoDialog,
} from "@/Composables";
import {
    AltProductCard,
    BaseSvg,
    Breadcrumbs,
    Divider,
    Overview,
    PartsBySeries,
    Pin,
    ProductSummary,
    RecordsNotFound,
    ResourceList,
    ScrollObserver,
    SearchInput,
    SideNav,
    SpecsTable,
    TableNew,
    TableProductImage,
    TableRowThree,
} from "@/Components";
import _ from "lodash";
import config from "@/config";
import ProductImageCarousel from "@/Components/Modules/ProductDetail/ProductImageCarousel.vue";

const { user } = useUser();
const route = inject("route");
const { toggleMobileOptionsDialog } = useMobileOptionsDialog();
const { toggleVideoDialog } = useVideoDialog();
const { togglePrintDialog, forceClose } = usePrintDialog();
const { toggleProductOptionsDialog } = useProductOptionsDialog();
const { toggleShareDialog } = useShareDialog();
const { getResource } = useResources();
const { marketingAssetUrl } = useAssetTransforms();
const { showNewMessage, openArticle } = useChat();
const props = defineProps([
    "card",
    "product",
    "errors",
    "crumbs",
    "pricing",
    "surcharge",
    "availability",
    "options",
    "resources",
    "components",
    "companions",
    "substitutes",
    "isLoadingMeta",
    "referer",
    "finish_swatch",
    "master_note_restriction",
]);

let hideCostDuringPrint = ref(false);

//refs
const partSearch = ref("");
const breadcrumbWrapper = ref();
const isScrolling = ref(false);
const resourceOpen = ref(false);
const isSeen = ref(false);
const isCurrent = ref(false);
const appIsScrolled = ref(false);
const computerSeen = ref(false);

const sections = computed(() => [
    {
        name: "Product",
        isHidden: false,
        isDisabled: false,
    },
    {
        name: "Specs",
        isHidden: false,
        isDisabled: !user.value,
    },
    {
        name: "Overview",
        isHidden: _.get(props, "product.series.descriptions", []).length === 0,
        isDisabled: !user.value,
    },
    {
        name: "Resources",
        isHidden: _.get(props, "resources", []).length === 0,
        isDisabled: !user.value,
    },
    {
        name: "Parts",
        isHidden: false,
        isDisabled: !user.value,
    },
    {
        name: "Related",
        isHidden: _.get(props, "companions", []).length === 0,
        isDisabled: !user.value,
    },
    {
        name: "Similar",
        isHidden: _.get(props, "substitutes", []).length === 0,
        isDisabled: !user.value,
    },
]);

const componentsColumns = [
    {
        name: "SKU",
        component: TableProductImage,
        props: ({ row }) => ({
            value: row,
            link: {
                name: "web.catalog.products.show",
            },
            preview: true,
            hideThumbnail: true,
        }),
    },
    {
        name: "Parent",
        path: "parent",
    },
    {
        name: "Qty",
        path: "comp_qty",
    },
];

//computed vars
const hasFinish = computed(() =>
    _.get(props, "product.typed_attributes.finish.0", false)
);

const finish = computed(() => {
    let hasFinishFamily = _.get(
        props,
        "product.typed_attributes.finish_family.0",
        null
    );
    if (hasFinish.value) {
        let finish = _.get(
            props,
            "product.attributes." + hasFinish.value,
            null
        );
        if (finish?.description) {
            return {
                image:
                    "https://d1w4srxztsilxj.cloudfront.net/media_v1_0/finishes/" +
                    finish.value.replace(/[\W_]+/u, "") +
                    ".jpg",
                value: finish.value,
                description: finish.description,
                family: _.get(
                    props,
                    "product.attributes." + hasFinishFamily,
                    null
                ),
            };
        }
    }
    return {};
});

const finishInfo = computed(() => {
    if (hasFinish.value) {
        return [
            {
                label: "Finish",
                value: finish.value?.value,
                route: {
                    name: "web.catalog.resources.index",
                },
            },
            {
                label: "Finish Description",
                value: finish.value?.description,
            },
            {
                label: "Finish Family",
                value: finish.value.family?.value,
            },
        ];
    }
    return [];
});

const primaryAttributes = computed(() => [
    {
        label: "Vendor",
        value: _.get(props, "product.vendor.parent", ""),
    },
    {
        label: "Manufacturer",
        value: _.get(props, "product.vendor.display_name", ""),
    },
    {
        label: "SKU",
        value: _.get(props, "product.sku", ""),
    },
    {
        label: "UID",
        value: _.get(props, "product.uid", ""),
    },
]);

const filteredComponents = computed(() => {
    if (!partSearch.value) {
        return props.components;
    }

    return props.components
        .slice()
        .map((row) => _.cloneDeep(row))
        .filter((row, i) => {

            let isMatchSku = row.comp_item
                ?.toLowerCase()
                .includes(partSearch.value.toLowerCase());
            let isMatchDescription = row.display_short_description
                ?.toLowerCase()
                .includes(partSearch.value.toLowerCase());

            row.sub_components = row.sub_components?.filter((sub_component) => {
                let case1 = sub_component.comp_item
                    ?.toLowerCase()
                    ?.includes(partSearch.value?.toLowerCase());
                let case2 = sub_component.display_short_description
                    ?.toLowerCase()
                    ?.includes(partSearch.value?.toLowerCase());

                return case1 || case2;
            });

            // If the part has been filtered out, but it contains some sub-components that match the search query.
            // Make sure to include the part with the filtered sub-components.
            return (
                isMatchSku || isMatchDescription || row.sub_components.length
            );
        });
});

const category_route = computed(() => {
    return route("web.catalog.products.index", {
        filters: {
            2: {
                field: "filter_category",
                values: [props.product?.tags["item-category"][0]?.name],
                type: "any",
            },
        },
    });
});

const series_route = computed(() => {
    return route("web.catalog.products.index", {
        filters: {
            1: {
                field: "filter_series",
                values: [props.product.series.name],
                type: "any",
            },
            2: {
                field: "filter_category",
                values: [_.get(props.product, "tags.item-category.0.name", "")],
                //values: [props.product?.tags["item-category"][0]?.name],
                type: "any",
            },
        },
    });
});

const sub_category_route = computed(() => {
    return route("web.catalog.products.index", {
        filters: {
            // 0: {
            //     field: 'filter_manufacturer',
            //     values: [props.product.vendor.display_name],
            //     type: 'any',
            // },
            // 1: {
            //     field: 'filter_series',
            //     values: [props.product.series.name],
            //     type: 'any',
            // },
            2: {
                field: "filter_category",
                values: [props.product.tags["item-category"][0]?.name],
                type: "any",
            },
            3: {
                field: "filter_sub_category",
                values: [props.product.tags["item-sub-category"][0]?.name],
                type: "any",
            },
        },
    });
});

//functions
function scrollToSection(name) {
    isCurrent.value = name;
    isScrolling.value = name;
    document.getElementById(name)?.scrollIntoView({ behavior: "smooth" });
}

function intersectionCallback(entries) {
    let newValue = isCurrent.value;

    entries.forEach((entry) => {
        let sticky = entry.target.nextElementSibling;
        if (sticky?.classList.contains("sticky")) {
            sticky.classList.toggle("is-pinned", entry.intersectionRatio < 1);
        }
        if (entry.target.dataset.section && isCurrent.value) {
            if (sections.value[entry.target.dataset.section]) {
                if (entry.isIntersecting) {
                    if (
                        isScrolling.value &&
                        isScrolling.value ===
                            sections.value[entry.target.dataset.section].name
                    ) {
                        isScrolling.value = false;
                    } else if (!isScrolling.value) {
                        newValue =
                            sections.value[entry.target.dataset.section].name;
                    }
                }
                if (sections.value[entry.target.dataset.section].handler) {
                    sections.value[entry.target.dataset.section].handler(
                        entry,
                        sections.value[entry.target.dataset.section]
                    );
                }
            }
        } else {
            newValue = sections.value[0].name;
        }
    });
    isCurrent.value = newValue;
}

//events
onMounted(() => {
    window.addEventListener("scroll", () => {
        window.scrollY > 20
            ? (appIsScrolled.value = true)
            : (appIsScrolled.value = false);
        appIsScrolled.value
            ? breadcrumbWrapper.value?.classList.add("shadow-md")
            : breadcrumbWrapper.value?.classList.remove("shadow-md");
    });
    let observer = new IntersectionObserver(intersectionCallback, {
        rootMargin: "-35% 0px -65%",
    });
    document.querySelectorAll(".sentinel").forEach(function (sentinel) {
        observer.observe(sentinel);
    });

    eventBus.on("hide-cost-during-print", function () {
        hideCostDuringPrint.value = true;
        setTimeout(() => {
            forceClose();
            window.print();
        }, 50);
    });
    eventBus.on("show-cost-during-print", function () {
        hideCostDuringPrint.value = false;
        setTimeout(() => {
            forceClose();
            window.print();
        }, 50);
    });

    document.body.classList.add("has-print-cover");
});

onBeforeUnmount(() => {
    document.body.classList.remove("has-print-cover");
    eventBus.off("hide-cost-during-print");
    eventBus.off("show-cost-during-print");
});
</script>

<style scoped>
.sticky_nav_top {
    top: var(--header-height);
}
</style>
